import React from 'react';
import { Scale, ShieldCheck, HardDrive, ArrowRight, User, Briefcase, FileText, Settings } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
interface LoginProps {
  onLogin: (personaId: string) => void;
}
export function Login({
  onLogin
}: LoginProps) {
  const personas = [{
    id: 'EF',
    name: 'Edward Festeryga',
    role: 'Managing Attorney / Trial Lead',
    initials: 'EF',
    icon: Briefcase,
    description: 'Executive dashboard with case risks, approvals, and critical evidence summaries.',
    defaultPage: 'Dashboard',
    highlight: true
  }, {
    id: 'AT',
    name: 'Ana Torres',
    role: 'Paralegal Reviewer',
    initials: 'AT',
    icon: FileText,
    description: 'Chronology review, document validation, provider timelines, and extraction quality checks.',
    defaultPage: 'Chronology',
    highlight: false
  }, {
    id: 'MS',
    name: 'Maria Santos',
    role: 'Intake Coordinator',
    initials: 'MS',
    icon: User,
    description: 'New matter setup, intake workflows, email filing, and folder organization.',
    defaultPage: 'Matters',
    highlight: false
  }, {
    id: 'RK',
    name: 'Robert Kim',
    role: 'Litigation Support',
    initials: 'RK',
    icon: Settings,
    description: 'Bates workflows, production classification, OCR monitoring, and audit queue.',
    defaultPage: 'Workflows',
    highlight: false
  }];
  return <div className="min-h-screen w-full flex flex-col items-center justify-center bg-slate-900 p-4 md:p-8" data-id="element-1331">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1332">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-red-900/20 blur-[120px]" data-id="element-1333" />
        <div className="absolute top-[60%] -right-[10%] w-[40%] h-[40%] rounded-full bg-slate-800/40 blur-[100px]" data-id="element-1334" />
      </div>

      <div className="w-full max-w-5xl relative z-10 flex flex-col items-center" data-id="element-1335">
        <div className="flex flex-col items-center mb-10 text-center" data-id="element-1336">
          <div className="bg-red-700 p-3 rounded-xl mb-4 shadow-lg shadow-red-900/50" data-id="element-1337">
            <Scale className="w-8 h-8 text-white" data-id="element-1338" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight font-heading" data-id="element-1339">
            Festeryga Law
          </h1>
          <p className="text-slate-400 text-base md:text-lg mt-2" data-id="element-1340">
            Local Legal Intelligence Platform
          </p>
          <p className="text-red-400 italic text-sm mt-2 font-medium" data-id="element-1341">
            When It's Everything™
          </p>
        </div>

        <div className="w-full mb-8 text-center" data-id="element-1342">
          <h2 className="text-xl font-semibold text-white mb-2" data-id="element-1343">
            Select Demo Persona
          </h2>
          <p className="text-slate-400 text-sm max-w-2xl mx-auto" data-id="element-1344">
            Experience the platform through different roles. Each persona has a
            tailored dashboard, specific permissions, and a unique default
            landing page.
          </p>
          <p className="text-slate-500 text-xs mt-3 italic" data-id="element-1345">
            Production deployment will use secure SSO authentication with
            role-based access controls.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-4xl" data-id="element-1346">
          {personas.map(persona => <Card key={persona.id} className={`border-slate-800 bg-slate-900/80 backdrop-blur-xl shadow-xl hover:shadow-2xl transition-all duration-300 group cursor-pointer overflow-hidden relative ${persona.highlight ? 'ring-2 ring-red-700/50 md:scale-[1.02]' : 'hover:border-slate-600'}`} onClick={() => onLogin(persona.id)} data-id="element-1347">
              {persona.highlight && <div className="absolute top-0 right-0 bg-red-700 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider z-10" data-id="element-1348">
                  Primary Demo
                </div>}
              <CardContent className="p-6 flex flex-col h-full" data-id="element-1349">
                <div className="flex items-start space-x-4 mb-4" data-id="element-1350">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold shrink-0 ${persona.highlight ? 'bg-red-700 text-white shadow-lg shadow-red-900/50' : 'bg-slate-800 text-slate-300 group-hover:bg-slate-700 transition-colors'}`} data-id="element-1351">
                    {persona.initials}
                  </div>
                  <div data-id="element-1352">
                    <h3 className="text-lg font-bold text-white group-hover:text-red-400 transition-colors" data-id="element-1353">
                      {persona.name}
                    </h3>
                    <p className="text-sm text-slate-400 font-medium flex items-center mt-0.5" data-id="element-1354">
                      <persona.icon className="w-3.5 h-3.5 mr-1.5" data-id="element-1355" />
                      {persona.role}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-slate-400 leading-relaxed mb-6 flex-1" data-id="element-1356">
                  {persona.description}
                </p>

                <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-800/50" data-id="element-1357">
                  <div className="text-xs text-slate-500" data-id="element-1358">
                    Starts at:{' '}
                    <span className="text-slate-300 font-medium" data-id="element-1359">
                      {persona.defaultPage}
                    </span>
                  </div>
                  <Button variant={persona.highlight ? 'primary' : 'secondary'} className={`h-8 text-xs ${persona.highlight ? 'bg-red-700 hover:bg-red-600 text-white' : 'bg-slate-800 text-white hover:bg-slate-700'}`} onClick={e => {
                e.stopPropagation();
                onLogin(persona.id);
              }} data-id="element-1360">
                    Enter as {persona.initials}{' '}
                    <ArrowRight className="w-3 h-3 ml-1.5" data-id="element-1361" />
                  </Button>
                </div>
              </CardContent>
            </Card>)}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 text-xs text-slate-500" data-id="element-1362">
          <div className="flex items-center" data-id="element-1363">
            <ShieldCheck className="w-4 h-4 mr-2 text-emerald-500/70" data-id="element-1364" />
            End-to-end encrypted connection
          </div>
          <div className="hidden sm:block w-1 h-1 rounded-full bg-slate-700" data-id="element-1365"></div>
          <div className="flex items-center" data-id="element-1366">
            <HardDrive className="w-4 h-4 mr-2 text-red-700/70" data-id="element-1367" />
            Data remains on local firm servers
          </div>
        </div>

        <div className="mt-8 text-center text-[11px] text-slate-600" data-id="element-1368">
          © {new Date().getFullYear()} All rights reserved. Platform designed &
          developed by{' '}
          <a href="https://pendoah.ai" target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300 font-medium transition-colors" data-id="element-1369">
            Pendoah.ai
          </a>
        </div>
      </div>
    </div>;
}